import {gql} from '@apollo/client'
import type {Status} from 'types/api/asyncTasks/asyncTask'

export type Order = 'desc' | 'asc'
export type SortColumn = 'created_at' | 'title' | 'created_user'

export type ContractsQueryVariables = {
  employeeId: string
  statuses: Status[]
}

export type ContractsQueryResult = {
  client: Client
}

export type Client = {
  id: string
  planType: string
  isVerified: boolean
  employee: Employee
}

export type Employee = {
  id: string
  displayFirstName: string
  displayLastName: string
  employeeDocumentContractRequests: EmployeeDocumentContractRequest[]
}

export type EmployeeDocumentContractRequest = {
  id: string
  startedAt: string
  contractInfo: {
    senderName: string
  }
}

export type User = {
  id: string
  email: string
  employee: {
    id: string
    displayFirstName: string
    displayLastName: string
  }
}

export type QueryVariables = {
  employeeId: string
  requestId: string
  page: number
  per: number
  search: Search
}

export type Search = {
  sortOrder?: Order
  sortColumn?: SortColumn
  q?: string
}

export type QueryResult = {
  client: {
    id: string
    employee: {
      id: string
      employeeDocumentContractRequest: ContractRequest
    }
  }
}

export type DeletedUser = {
  id: string
  email: string
  employee: {
    id: string
    displayFirstName: string
    displayLastName: string
  }
}

export type ContractRequest = {
  id: string
  contractInfo: ContractInfo
  searchDocuments: {
    totalCount: number
    list: Document[]
  }
}

export type ContractInfo = {
  title: string
  senderName: string
  senderOfficeName: string
  comment: string
  status: ContractInfoStatus
  downloadableSignedDocument: boolean
  deletedUser: DeletedUser
  contractOriginalSenderInfo: string | null
}

export type ContractInfoStatus = 'signed' | 'unsigned' | 'canceled'

export type Document = {
  id: string
  createdAt: string
  title: string
  createdUser: User
}

export const CONTRACTS = gql`
  query contractsRequest($employeeId: ID!, $statuses: [AsyncTaskStatus!]) {
    client {
      id
      planType
      isVerified
      employee(employeeId: $employeeId) {
        id
        displayLastName
        displayFirstName
        employeeDocumentContractRequests(statuses: $statuses) {
          id
          startedAt
          contractInfo {
            senderName
          }
        }
      }
    }
  }
`

const DOCUMENT_CONTRACT_REQUEST_FRAGMENT = gql`
  fragment DocumentContractRequestField on EmployeeDocumentContractRequest {
    id
    contractInfo {
      title
      senderName
      senderOfficeName
      comment
      status
      downloadableSignedDocument
      contractOriginalSenderInfo
    }
    searchDocuments(per: $per, page: $page, search: $search) {
      totalCount
      list {
        id
        createdAt
        title
        createdUser {
          id
          email
          employee {
            id
            displayFirstName
            displayLastName
          }
        }
      }
    }
  }
`

export const CONTRACT = gql`
  query contractRequest(
    $employeeId: ID!
    $requestId: ID!
    $per: Int
    $page: Int
    $search: EmployeeDocumentContractSearchInput
  ) {
    client {
      id
      employee(employeeId: $employeeId) {
        id
        employeeDocumentContractRequest(requestId: $requestId) {
          ...DocumentContractRequestField
        }
      }
    }
  }
  ${DOCUMENT_CONTRACT_REQUEST_FRAGMENT}
`
